import { ENVIRONMENT_VALUES } from '../constants/ENVIRONMENT_VALUES';
import { getEnvironmentValue } from './getEnvironmentValue';
import { tryCatch, ok, err } from '../../error/functions';
import type { Result, AppError } from '../../error/types';

/**
 * @group Functions
 * @summary <blockquote>Determines if the current execution context is during build time.</blockquote>
 * 
 * @returns Result containing true if in build time, false otherwise
 * 
 * @example
 * ```typescript
 * import { isBuildTime } from '@shared/utils';
 * 
 * const result = isBuildTime();
 * result.match({
 *   ok: (isBuild) => {
 *     if (isBuild) {
 *       // Skip runtime-only operations during build
 *       return;
 *     }
 *   },
 *   err: (error) => console.error(`Detection failed: ${error.errorMessage}`)
 * });
 * ```
 */
export function isBuildTime(): Result<boolean, AppError> {
  return tryCatch(() => {
    // Check if we're on the server-side
    if (typeof window !== 'undefined') {
      return false;
    }

    // Check if process is available
    if (typeof process === 'undefined') {
      return false;
    }

    // First, check for Next.js build phase (works in all environments)
    const nextPhaseResult = getEnvironmentValue(ENVIRONMENT_VALUES.BUILD.NEXT_PHASE);
    if (nextPhaseResult.isOk && nextPhaseResult.value === 'phase-production-build') {
      return true;
    }

    // Mobile-specific build detection
    // Check for React Native environment indicators
    const isReactNative = typeof navigator !== 'undefined' &&
      navigator.product === 'ReactNative';

    // For mobile builds, we can detect based on environment or custom variables
    // This is a simplified approach that works for most mobile build scenarios
    if (isReactNative) {
      // Check for common mobile build indicators using direct process.env access
      // (This is acceptable for build-time detection as it's not runtime logic)
      const isMobileBuild = process.env.EXPO_PUBLIC_BUILD_MODE === 'build' ||
        process.env.METRO_BUILD === 'true' ||
        process.env.REACT_NATIVE_BUNDLE === 'true' ||
        process.env.MOBILE_BUILD === 'true' ||
        process.env.NATIVE_BUILD === 'true';

      if (isMobileBuild) {
        return true;
      }
    }

    // Skip process.argv check entirely in Edge Runtime environments
    // Edge Runtime doesn't support process.argv, so we rely only on environment variables

    // Check if we're in production mode without deployment environment
    const nodeEnvResult = getEnvironmentValue(ENVIRONMENT_VALUES.LOGGING.NEXT_PUBLIC_NODE_ENV);
    const vercelEnvResult = getEnvironmentValue(ENVIRONMENT_VALUES.LOGGING.NEXT_PUBLIC_VERCEL_ENV);
    const railwayEnvResult = getEnvironmentValue(ENVIRONMENT_VALUES.BUILD.RAILWAY_ENVIRONMENT);

    if (nodeEnvResult.isOk && nodeEnvResult.value === 'production') {
      if ((!vercelEnvResult.isOk || !vercelEnvResult.value) &&
        (!railwayEnvResult.isOk || !railwayEnvResult.value)) {
        return true;
      }
    }

    return false;
  }).match({
    ok: (result) => ok(result),
    err: (error) => err({
      errorCode: 'BUILD_TIME_DETECTION_FAILED',
      errorMessage: `Failed to detect build time: ${error.errorMessage}`
    })
  });
} 